import React from 'react'

interface PricingFeature {
  text: string
  included: boolean
}

interface PricingCardProps {
  planType: string
  price: string
  period: string
  features: PricingFeature[]
  buttonText: string
  isPopular?: boolean
  icon: React.ReactNode
  onButtonClick?: () => void
  /** When true, show a "Contact us" CTA instead of a price + checkout button. */
  customPricing?: boolean
  /** Optional descriptive blurb shown under the price for custom-pricing plans. */
  description?: string | undefined
  /** External URL the custom-pricing CTA links to (e.g. a help/contact page). */
  ctaUrl?: string | undefined
}

const ArrowIcon = () => (
  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M9 5l7 7-7 7'
    />
  </svg>
)

const PricingCard: React.FC<PricingCardProps> = ({
  planType,
  price,
  period,
  features,
  buttonText,
  isPopular = false,
  icon: _icon, // Intentionally unused but part of interface
  onButtonClick,
  customPricing = false,
  description,
  ctaUrl,
}) => {
  return (
    <div className='bg-white rounded-2xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-100 relative h-full flex flex-col w-full max-w-full'>
      {/* Plan Header */}
      <div className='flex items-center justify-between mb-4 sm:mb-6'>
        <div className='flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1'>
          <span className='text-gray-600 text-sm font-medium text-start truncate'>{planType}</span>
        </div>
        {isPopular && (
          <span className='text-gray-600 text-sm font-medium flex-shrink-0 ml-2'>Popular</span>
        )}
      </div>

      {/* Price */}
      <div className='mb-5 sm:mb-6 md:mb-8'>
        {customPricing ? (
          <span className='text-3xl sm:text-4xl font-bold text-gray-900'>
            Contact us
          </span>
        ) : (
          <div className='flex items-baseline space-x-1 flex-wrap'>
            <span className='text-3xl sm:text-4xl font-bold text-gray-900'>{price}</span>
            <span className='text-gray-500 text-xs sm:text-sm'>/{period}</span>
          </div>
        )}
        {customPricing && description && (
          <p className='mt-2 text-gray-500 text-xs sm:text-sm leading-relaxed text-start'>
            {description}
          </p>
        )}
      </div>

      {/* Features */}
      <div className='mb-6 sm:mb-8 flex-grow min-h-0'>
        <h4 className='text-gray-900 font-medium mb-3 sm:mb-4 text-start text-sm sm:text-base'>
          What you will get:
        </h4>
        <ul className='space-y-2.5 sm:space-y-3'>
          {features.map((feature, index) => (
            <li key={index} className='flex items-start space-x-2 sm:space-x-3'>
              <div className='w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0'>
                {feature.included ? (
                  <svg
                    className='w-4 h-4 text-green-500'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                ) : (
                  <svg
                    className='w-4 h-4 text-gray-300'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                )}
              </div>
              <span className='text-gray-600 text-xs sm:text-sm leading-relaxed text-start flex-1'>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Button */}
      <div className='mt-auto'>
        {customPricing && ctaUrl ? (
          <a
            href={ctaUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={e => {
              // Open in the system browser. Inside an installed PWA the URL is
              // in-scope, so a plain target=_blank would open it in the app
              // window — window.open() breaks out to the browser instead.
              e.preventDefault()
              window.open(ctaUrl, '_blank', 'noopener,noreferrer')
            }}
            className='w-full bg-gray-900 text-white py-3 px-4 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center text-center whitespace-nowrap'
          >
            <span>{buttonText}</span>
          </a>
        ) : isPopular ? (
          <button
            onClick={onButtonClick}
            className='w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center space-x-2'
          >
            <span>{buttonText}</span>
            <ArrowIcon />
          </button>
        ) : (
          <button
            onClick={onButtonClick}
            className='w-full bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center space-x-2'
          >
            <span>{buttonText}</span>
            <ArrowIcon />
          </button>
        )}
      </div>
    </div>
  )
}

export default PricingCard
